import { moveDecimalPoint, trimObject } from 'utils';
import { SecondaryDealFilterValues, SecondaryDealFilterPayload } from './types';
import { FilterTransformHelpers } from '../types';
import { ALL_CURRENCIES_VALUE } from '../constants';

class SecondaryDealFiltersHelper
  implements FilterTransformHelpers<SecondaryDealFilterPayload, SecondaryDealFilterValues>
{
  getEmptyValue = (): SecondaryDealFilterValues => {
    return {
      productType: [],
      rating: [],
      currency: [],
    };
  };

  getPayloadFromValue = (values: SecondaryDealFilterValues): SecondaryDealFilterPayload => {
    return trimObject({
      productType: values.productType?.length ? values.productType : undefined,
      rating: values.rating?.length ? values.rating : undefined,
      currency: (() => {
        const currencyValues = values.currency as string[] | undefined;

        if (!currencyValues?.length) {
          return undefined;
        }

        return currencyValues.includes(ALL_CURRENCIES_VALUE)
          ? [ALL_CURRENCIES_VALUE]
          : values.currency;
      })(),
      minDuration: values.minDuration,
      maxDuration: values.maxDuration,
      minPrincipal: values.minPrincipal
        ? moveDecimalPoint(values.minPrincipal, 2)
        : values.minPrincipal,
      maxPrincipal: values.maxPrincipal
        ? moveDecimalPoint(values.maxPrincipal, 2)
        : values.maxPrincipal,
      minNominalInterestRate: values.minNominalInterestRate
        ? moveDecimalPoint(values.minNominalInterestRate, -2)
        : values.minNominalInterestRate,
      maxNominalInterestRate: values.maxNominalInterestRate
        ? moveDecimalPoint(values.maxNominalInterestRate, -2)
        : values.maxNominalInterestRate,
      minDefaultRate: values.minDefaultRate
        ? moveDecimalPoint(values.minDefaultRate, -2)
        : values.minDefaultRate,
      maxDefaultRate: values.maxDefaultRate
        ? moveDecimalPoint(values.maxDefaultRate, -2)
        : values.maxDefaultRate,
      minDiscount: values.minDiscount
        ? moveDecimalPoint(values.minDiscount, -2)
        : values.minDiscount,
      maxDiscount: values.maxDiscount
        ? moveDecimalPoint(values.maxDiscount, -2)
        : values.maxDiscount,
      sortBy: values.sort?.sortBy,
      sortOrder: values.sort?.sortOrder,
    });
  };

  getValueFromPayload = (
    payload: Partial<SecondaryDealFilterPayload>,
  ): SecondaryDealFilterValues => {
    return trimObject({
      productType: Array.isArray(payload.productType) ? payload.productType : [],
      // rating: Array.isArray(payload.rating) ? payload.rating : [],
      currency: Array.isArray(payload.currency) ? payload.currency : [],
      minDuration: payload.minDuration,
      maxDuration: payload.maxDuration,
      minPrincipal: payload.minPrincipal
        ? moveDecimalPoint(payload.minPrincipal, -2)
        : payload.minPrincipal,
      maxPrincipal: payload.maxPrincipal
        ? moveDecimalPoint(payload.maxPrincipal, -2)
        : payload.maxPrincipal,
      minNominalInterestRate: payload.minNominalInterestRate
        ? moveDecimalPoint(payload.minNominalInterestRate, 2)
        : payload.minNominalInterestRate,
      maxNominalInterestRate: payload.maxNominalInterestRate
        ? moveDecimalPoint(payload.maxNominalInterestRate, 2)
        : payload.maxNominalInterestRate,
      minDefaultRate: payload.minDefaultRate
        ? moveDecimalPoint(payload.minDefaultRate, 2)
        : payload.minDefaultRate,
      maxDefaultRate: payload.maxDefaultRate
        ? moveDecimalPoint(payload.maxDefaultRate, 2)
        : payload.maxDefaultRate,
      minDiscount: payload.minDiscount
        ? moveDecimalPoint(payload.minDiscount, 2)
        : payload.minDiscount,
      maxDiscount: payload.maxDiscount
        ? moveDecimalPoint(payload.maxDiscount, 2)
        : payload.maxDiscount,

      // prettier-ignore
      sort: payload.sortBy && payload.sortOrder
        ? { sortBy: payload.sortBy, sortOrder: payload.sortOrder }
        : undefined,
    });
  };

  countAmountValues = (values: SecondaryDealFilterValues) => {
    let counter = 0;

    if (typeof values.minPrincipal === 'number') counter += 1;
    if (typeof values.maxPrincipal === 'number') counter += 1;

    return counter;
  };

  countDurationValues = (values: SecondaryDealFilterValues) => {
    let counter = 0;

    if (typeof values.minDuration === 'number') counter += 1;
    if (typeof values.maxDuration === 'number') counter += 1;

    return counter;
  };

  countNominalInterestRateValues = (values: SecondaryDealFilterValues) => {
    let counter = 0;

    if (typeof values.minNominalInterestRate === 'number') counter += 1;
    if (typeof values.maxNominalInterestRate === 'number') counter += 1;

    return counter;
  };

  countDefaultRateValues = (values: SecondaryDealFilterValues) => {
    let counter = 0;

    if (typeof values.minDefaultRate === 'number') counter += 1;
    if (typeof values.maxDefaultRate === 'number') counter += 1;

    return counter;
  };

  countDiscountValues = (values: SecondaryDealFilterValues) => {
    let counter = 0;

    if (typeof values.minDiscount === 'number') counter += 1;
    if (typeof values.maxDiscount === 'number') counter += 1;

    return counter;
  };

  countValues = (values: SecondaryDealFilterValues) => {
    let counter = 0;

    counter += values.productType?.length ?? 0;
    counter += values.rating?.length ?? 0;
    counter += this.countDurationValues(values);
    counter += this.countAmountValues(values);
    counter += this.countNominalInterestRateValues(values);
    counter += this.countDefaultRateValues(values);
    counter += this.countDiscountValues(values);

    return counter;
  };
}

export const secondaryDealFiltersHelper = new SecondaryDealFiltersHelper();
